import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  X as XIcon,
  FolderOpen as FolderIcon,
  FileDown as FileDownIcon,
  Share2 as ShareIcon,
  RotateCcw as RotateCcwIcon,
} from "lucide-react";
import { useOptimizer } from "../hooks/useOptimizer";
import { useProjectAutosave } from "../hooks/useProjectAutosave";
import { useUserPlan } from "../hooks/useUserPlan";
import { SummaryPanel } from "../components/editor/SummaryPanel";
import { UserMenu } from "../components/editor/UserMenu";
import { OnboardingTour } from "../components/editor/OnboardingTour";

// M-11 — lazy-load the Konva canvas + share dialog. Konva is ~94 kB
// gzipped; editor shell renders fast and the canvas streams in when
// the first optimizer result arrives. Share dialog only loads when
// the user clicks Share.
const SheetCanvas = lazy(() =>
  import("../components/canvas/SheetCanvas").then((m) => ({
    default: m.SheetCanvas,
  })),
);
const ShareDialog = lazy(() =>
  import("../components/editor/ShareDialog").then((m) => ({
    default: m.ShareDialog,
  })),
);
import { cn } from "../lib/cn";
import {
  createProject,
  getProject,
  isGuestProjectId,
  listProjects,
} from "../lib/api/projects";
import type { Sheet, Part, Units, Layout } from "../types";

type MobileTab = "parts" | "canvas" | "summary";

const MOBILE_TAB_IDS: readonly MobileTab[] = ["parts", "canvas", "summary"];

// E-06 debounce for live recalc
const RECALC_DEBOUNCE_MS = 300;

const DEFAULT_SHEETS: Sheet[] = [
  { id: "s1", width: 2440, height: 1220, quantity: 5, costPerUnit: 800 },
];
const DEFAULT_PARTS: Part[] = [
  { id: "p1", width: 600, height: 400, quantity: 4, label: "Door", rotatable: true },
  { id: "p2", width: 300, height: 200, quantity: 8, label: "Shelf", rotatable: true },
];

export function EditorPage() {
  const { t } = useTranslation();
  const { id: routeProjectId } = useParams<{ id: string }>();
  const [projectId, setProjectId] = useState<string | null>(routeProjectId ?? null);
  const [projectName, setProjectName] = useState("Untitled project");
  const [units] = useState<Units>("metric");
  const [kerf, setKerf] = useState(3);
  const [edgePadding, setEdgePadding] = useState(10);
  const [sheets, setSheets] = useState<Sheet[]>(DEFAULT_SHEETS);
  const [parts, setParts] = useState<Part[]>(DEFAULT_PARTS);
  const [activeMobileTab, setActiveMobileTab] = useState<MobileTab>("parts");
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadedProjectRef = useRef<string | null>(null);

  // E-08 — manual layout overrides (drag part). Lives above the autosave
  // hook so the hook's patch closure can include it.
  const [manualLayout, setManualLayout] = useState<Layout | null>(null);

  const { run, result, error, isRunning } = useOptimizer();

  // M-04 — plan-aware PDF export. Hook is cheap (React Query cache hit
  // after first fetch). Defaults to 'free' while loading so the watermark
  // is always applied conservatively until we know the real plan.
  const { plan } = useUserPlan();

  // A-05 — load project from route on mount / id change
  useEffect(() => {
    const id = routeProjectId;
    if (!id || loadedProjectRef.current === id) return;
    let mounted = true;
    (async () => {
      const r = await getProject(id);
      if (!mounted) return;
      if (r.ok) {
        setProjectId(r.data.id);
        setProjectName(r.data.name);
        setKerf(r.data.kerf);
        setEdgePadding(r.data.edgePadding);
        setSheets(r.data.sheets.length > 0 ? r.data.sheets : DEFAULT_SHEETS);
        setParts(r.data.parts.length > 0 ? r.data.parts : DEFAULT_PARTS);
        loadedProjectRef.current = r.data.id;
        setLoadError(null);
      } else {
        setLoadError(r.error.message);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [routeProjectId]);

  // On a bare /app visit (no :id in the route), either open the user's
  // most-recently-updated project OR, if they have none, create one
  // and land there. Before this, every login created a brand-new
  // untitled project — users couldn't find the project they had
  // been working on.
  const navigate = useNavigate();
  useEffect(() => {
    if (projectId) return;
    if (routeProjectId) return; // route hints load-in-progress
    let mounted = true;
    (async () => {
      const list = await listProjects();
      if (!mounted) return;

      if (list.ok && list.data.length > 0) {
        const mostRecent = list.data[0]!;
        navigate(`/app/project/${mostRecent.id}`, { replace: true });
        return;
      }

      // First-time user: seed a project so the editor has something
      // to autosave into.
      const r = await createProject({
        name: projectName,
        units,
        kerf,
        edgePadding,
        sheets,
        parts,
      });
      if (!mounted) return;
      if (r.ok) {
        setProjectId(r.data.id);
        loadedProjectRef.current = r.data.id;
        navigate(`/app/project/${r.data.id}`, { replace: true });
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A-05 — debounced autosave on any editor change
  const { status: saveStatus } = useProjectAutosave(
    projectId,
    {
      name: projectName,
      units,
      kerf,
      edgePadding,
      sheets,
      parts,
      layout: manualLayout ?? result?.layout,
    },
    { delayMs: 1000, disabled: !!loadError },
  );

  // E-06 — debounced auto-recalc on any input change.
  useEffect(() => {
    if (parts.length === 0 || sheets.length === 0) return;
    const t = window.setTimeout(() => {
      run({ sheets, parts, kerf, edgePadding, units });
    }, RECALC_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [parts, sheets, kerf, edgePadding, units, run]);

  // Lookup: part family → label, for the unplaced banner
  const partLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of parts) m.set(p.id, p.label ?? p.id);
    return m;
  }, [parts]);

  const updatePart = (i: number, patch: Partial<Part>) => {
    setParts((prev) => {
      const next = [...prev];
      next[i] = { ...next[i]!, ...patch };
      return next;
    });
  };

  const deletePart = (i: number) => {
    setParts((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));
  };

  const addPart = () => {
    setParts((prev) => [
      ...prev,
      {
        id: `p${Date.now()}`,
        width: 100,
        height: 100,
        quantity: 1,
        rotatable: true,
      },
    ]);
  };

  const handleOptimize = () => {
    run({ sheets, parts, kerf, edgePadding, units });
  };

  // P-03 / P-05 — share link dialog
  const [shareOpen, setShareOpen] = useState(false);

  // Clear manual overrides ONLY when the user edits the inputs (parts,
  // sheets, kerf, padding) — the coordinates in manualLayout could
  // reference parts that were deleted or resized. Must NOT clear on
  // result-change, otherwise every 300ms auto-recalc wipes the drag.
  useEffect(() => {
    setManualLayout(null);
  }, [parts, sheets, kerf, edgePadding]);

  const activeLayout = manualLayout ?? result?.layout ?? null;

  // E-08 — drag handler. Returns true if the move is accepted.
  const handlePartMove = (
    partId: string,
    nextX: number,
    nextY: number,
  ): boolean => {
    if (!activeLayout) return false;
    const placed = activeLayout.placed;
    const idx = placed.findIndex((p) => p.partId === partId);
    if (idx < 0) return false;
    const current = placed[idx]!;

    // Only within-sheet drag is supported in MVP.
    const sheet = sheets[0];
    if (!sheet) return false;
    const usableW = sheet.width - 2 * edgePadding;
    const usableH = sheet.height - 2 * edgePadding;

    // Clamp to usable area. Reject if the part would exceed bounds even
    // after clamping — means the user dragged the origin well outside.
    if (nextX < 0 || nextY < 0) return false;
    if (nextX + current.width > usableW) return false;
    if (nextY + current.height > usableH) return false;

    // Overlap check against siblings on the same sheet. Kerf is treated
    // as a minimum gap between part edges.
    const next = { ...current, x: nextX, y: nextY };
    for (let j = 0; j < placed.length; j++) {
      if (j === idx) continue;
      const other = placed[j]!;
      if (other.sheetIndex !== next.sheetIndex) continue;
      const overlapsX =
        next.x < other.x + other.width + kerf &&
        other.x < next.x + next.width + kerf;
      const overlapsY =
        next.y < other.y + other.height + kerf &&
        other.y < next.y + next.height + kerf;
      if (overlapsX && overlapsY) return false;
    }

    // Commit
    const nextPlaced = placed.slice();
    nextPlaced[idx] = next;
    setManualLayout({ ...activeLayout, placed: nextPlaced });
    return true;
  };

  const resetManualLayout = () => setManualLayout(null);

  // P-01 — PDF export
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const handleExportPdf = async () => {
    if (!activeLayout) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const { exportLayoutPdf } = await import("../lib/pdf/export");
      const bytes = await exportLayoutPdf({
        layout: activeLayout,
        sheets,
        edgePadding,
        kerf,
        projectName,
        isFree: plan === "free", // M-04 — watermark for free plan only
      });
      // Copy into a fresh ArrayBuffer-backed Uint8Array. pdf-lib's save()
      // can return a subarray view; Safari's Blob constructor occasionally
      // gives zero-byte PDFs in that case. A fresh copy avoids it.
      const buffer = new Uint8Array(bytes.byteLength);
      buffer.set(bytes);
      const blob = new Blob([buffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const filename = `${(projectName || "cutmate-layout")
        .replace(/[^a-z0-9-]+/gi, "-")
        .toLowerCase()}.pdf`;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();

      // Defer cleanup so Safari / Firefox have time to start the
      // download before we yank the Blob URL out from under them.
      window.setTimeout(() => {
        if (a.parentNode) a.parentNode.removeChild(a);
        URL.revokeObjectURL(url);
      }, 2000);
    } catch (err) {
      console.error("PDF export failed:", err);
      setExportError(
        err instanceof Error ? err.message : "PDF export failed. Try again.",
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <div className="flex min-w-0 items-center gap-3">
          <span className="font-bold text-brand-700 dark:text-brand-100">
            CutMate
          </span>
          <input
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            aria-label="Project name"
            className="min-w-0 flex-1 truncate bg-transparent text-sm font-medium outline-none focus:ring-1 focus:ring-brand-500 md:text-base"
          />
          <SaveBadge status={saveStatus} />
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {manualLayout && (
            <button
              type="button"
              className="btn-ghost inline-flex items-center gap-1 px-2 text-xs"
              onClick={resetManualLayout}
              aria-label={t("editor.resetLayout")}
              title={t("editor.resetLayout")}
            >
              <RotateCcwIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{t("editor.resetLayout")}</span>
            </button>
          )}
          <button
            className="btn-ghost inline-flex items-center gap-1 px-2 sm:px-3"
            onClick={handleExportPdf}
            disabled={!activeLayout || isExporting}
            aria-label={t("editor.exportPdf")}
            title={t("editor.exportPdf")}
          >
            <FileDownIcon className="h-4 w-4" />
            <span className="hidden sm:inline">
              {isExporting ? t("editor.exporting") : t("editor.exportPdf")}
            </span>
          </button>
          <button
            className="btn-ghost inline-flex items-center gap-1 px-2 sm:px-3"
            onClick={() => setShareOpen(true)}
            disabled={!projectId || isGuestProjectId(projectId)}
            aria-label={t("editor.share")}
            title={t("editor.share")}
          >
            <ShareIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{t("editor.share")}</span>
          </button>
          <button
            className="btn-primary px-3 sm:px-4"
            onClick={handleOptimize}
            disabled={isRunning}
          >
            {isRunning ? t("editor.optimizing") : t("editor.optimize")}
          </button>
          <Link
            to="/projects"
            className="btn-ghost hidden items-center gap-1 sm:inline-flex"
          >
            <FolderIcon className="h-4 w-4" />
            {t("nav.projects")}
          </Link>
          <UserMenu />
        </div>
      </header>

      {shareOpen && (
        <Suspense fallback={null}>
          <ShareDialog
            projectId={projectId}
            open={shareOpen}
            onClose={() => setShareOpen(false)}
          />
        </Suspense>
      )}
      {loadError && (
        <div
          role="alert"
          className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
        >
          Couldn't load this project ({loadError}). Edits won't save until you
          refresh.
        </div>
      )}
      {exportError && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 border-b border-red-300 bg-red-50 px-4 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100"
        >
          <span>PDF export failed: {exportError}</span>
          <button
            type="button"
            onClick={() => setExportError(null)}
            className="text-xs underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="grid flex-1 grid-cols-1 pb-16 md:grid-cols-[320px_1fr_320px] md:pb-0">
        {/* Parts panel (E-04) */}
        <aside
          id="panel-parts"
          role="tabpanel"
          aria-labelledby="tab-parts"
          className={cn(
            "overflow-y-auto border-r border-slate-200 p-4 dark:border-slate-700",
            activeMobileTab === "parts" ? "block" : "hidden",
            "md:block",
          )}
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Parts
          </h2>
          <div className="mt-3 space-y-2">
            {parts.map((p, i) => (
              <div
                key={p.id}
                className="rounded border border-slate-200 p-2 text-sm dark:border-slate-700"
              >
                <div className="mb-1 flex items-center gap-2">
                  <input
                    className="input flex-1"
                    value={p.label ?? ""}
                    placeholder="Label"
                    aria-label={`Part ${i + 1} label`}
                    onChange={(e) => updatePart(i, { label: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => deletePart(i)}
                    disabled={parts.length <= 1}
                    aria-label={`Delete part ${p.label ?? p.id}`}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 dark:hover:bg-red-950"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className="input w-16"
                    aria-label={`Part ${i + 1} width`}
                    min={1}
                    value={p.width}
                    onChange={(e) => updatePart(i, { width: Number(e.target.value) })}
                  />
                  <span>×</span>
                  <input
                    type="number"
                    className="input w-16"
                    aria-label={`Part ${i + 1} height`}
                    min={1}
                    value={p.height}
                    onChange={(e) => updatePart(i, { height: Number(e.target.value) })}
                  />
                  <span className="text-xs text-slate-500">qty</span>
                  <input
                    type="number"
                    className="input w-14"
                    aria-label={`Part ${i + 1} quantity`}
                    min={1}
                    value={p.quantity}
                    onChange={(e) => updatePart(i, { quantity: Number(e.target.value) })}
                  />
                </div>
                <label className="mt-2 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                  <input
                    type="checkbox"
                    checked={p.rotatable}
                    onChange={(e) => updatePart(i, { rotatable: e.target.checked })}
                  />
                  Rotatable (grain not locked)
                </label>
              </div>
            ))}
            <button className="btn-ghost w-full" onClick={addPart}>
              + Add part
            </button>
          </div>
        </aside>

        {/* Canvas panel — Konva (E-02 + E-03) */}
        <main
          id="panel-canvas"
          role="tabpanel"
          aria-labelledby="tab-canvas"
          className={cn(
            "bg-slate-50 dark:bg-slate-900",
            activeMobileTab === "canvas" ? "block" : "hidden",
            "md:block",
          )}
        >
          {error ? (
            <div className="flex h-full items-center justify-center text-red-600">
              {error}
            </div>
          ) : activeLayout ? (
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center text-sm text-slate-500">
                  {t("editor.optimizing")}
                </div>
              }
            >
              <SheetCanvas
                layout={activeLayout}
                sheets={sheets}
                edgePadding={edgePadding}
                partLabelById={partLabelById}
                onPartMove={handlePartMove}
              />
            </Suspense>
          ) : (
            <div className="flex h-full items-center justify-center text-slate-500">
              {isRunning ? t("editor.optimizing") : t("editor.emptyCanvas")}
            </div>
          )}
        </main>

        {/* Summary + Settings panel (E-05 + E-07 + E-13) */}
        <aside
          id="panel-summary"
          role="tabpanel"
          aria-labelledby="tab-summary"
          className={cn(
            "overflow-y-auto border-l border-slate-200 p-4 dark:border-slate-700",
            activeMobileTab === "summary" ? "block" : "hidden",
            "md:block",
          )}
        >
          <SummaryPanel
            layout={result?.layout ?? null}
            unplaced={result?.unplaced ?? []}
            partLabelById={partLabelById}
            sheets={sheets}
            setSheets={setSheets}
            kerf={kerf}
            setKerf={setKerf}
            edgePadding={edgePadding}
            setEdgePadding={setEdgePadding}
            isRunning={isRunning}
          />
        </aside>
      </div>

      {/* Mobile tab bar — fixed at bottom */}
      <nav
        role="tablist"
        aria-label="Editor sections"
        className="fixed bottom-0 left-0 right-0 z-50 flex border-t border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-900 md:hidden"
      >
        {MOBILE_TAB_IDS.map((tabId) => {
          const isActive = activeMobileTab === tabId;
          return (
            <button
              key={tabId}
              id={`tab-${tabId}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${tabId}`}
              onClick={() => setActiveMobileTab(tabId)}
              className={cn(
                "flex-1 rounded-md px-4 py-3 text-sm font-semibold transition-colors min-h-[56px]",
                isActive
                  ? "bg-brand-700 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800",
              )}
            >
              {t(`mobileTabs.${tabId}`)}
            </button>
          );
        })}
      </nav>

      <OnboardingTour />
    </div>
  );
}

function SaveBadge({
  status,
}: {
  status: "idle" | "saving" | "saved" | "error";
}) {
  const { t } = useTranslation();
  const tone =
    status === "error"
      ? "text-red-600"
      : status === "saving"
        ? "text-slate-500"
        : "text-slate-400";
  const label =
    status === "saving"
      ? t("editor.saving")
      : status === "saved"
        ? t("editor.saved")
        : status === "error"
          ? t("editor.saveFailed")
          : "";
  if (!label) return null;
  return (
    <span className={`hidden text-xs ${tone} md:inline-block`} aria-live="polite">
      {label}
    </span>
  );
}
